import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import { MapPin, Package, CheckCircle, Clock, Menu, X, Settings, HelpCircle, LogOut, RefreshCw, ChevronRight, Truck, XCircle, Navigation, AlertCircle, UtensilsCrossed, Store, ChevronDown } from 'lucide-react';
import { useToast } from '../../components/Toast';
import { SkeletonRow } from '../../components/Skeleton';
import { APP_ROUTES } from '../../utils/auth';
import { clearStoredProfileSession, getStoredProfile, getProfileForRole, getStoredProfiles, setStoredProfile, setStoredProfiles } from '../../utils/session';
import { DELIVERY_COLUMNS } from '../../services/deliveries';
import { DELIVERY_STATUS_THEME } from '../../constants/deliveryStatus';
import { formatOrderTime } from '../../utils/orderTime';

const statusConfig = {
  pending:   { ...DELIVERY_STATUS_THEME.pending, icon: Clock },
  transit:   { ...DELIVERY_STATUS_THEME.transit, icon: Truck },
  delivered: { ...DELIVERY_STATUS_THEME.delivered, icon: CheckCircle },
  failed:    { ...DELIVERY_STATUS_THEME.failed, icon: XCircle },
};

// Calcul distance à vol d'oiseau (Haversine)
function haversine(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Tarif selon la distance
function getZoneTarif(km) {
  if (km < 5)  return { price: 1000, label: `Zone 1 · Livraison proche`,        detail: '< 5 km',       color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0' };
  if (km < 10) return { price: 1500, label: `Zone 2 · Quartier adjacent`,        detail: '5 – 10 km',    color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe' };
  if (km < 18) return { price: 2000, label: `Zone 3 · Inter-communes`,           detail: '10 – 18 km',   color: '#c2410c', bg: '#fff7ed', border: '#fed7aa' };
  return          { price: 2500, label: `Zone 4 · Grand Dakar / Banlieue`,   detail: '≥ 18 km',     color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe' };
}

// Géocodage via Nominatim (OpenStreetMap, gratuit)
async function geocode(query) {
  const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query + ', Dakar, Sénégal')}&countrycodes=sn&format=json&limit=5&addressdetails=0`;
  const res = await fetch(url, { headers: { 'Accept-Language': 'fr' } });
  return await res.json();
}

export default function ParticulierApp() {
  const navigate = useNavigate();
  const toast = useToast();
  const [profile, setProfile] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [loadingDeliveries, setLoadingDeliveries] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedService, setSelectedService] = useState('livraison');
  const [allProfiles, setAllProfiles] = useState(() => getStoredProfiles());

  // Devenir vendeur
  const [showVendeurForm, setShowVendeurForm] = useState(false);
  const [vendeurStoreName, setVendeurStoreName] = useState('');
  const [vendeurLoading, setVendeurLoading] = useState(false);
  const [vendeurError, setVendeurError] = useState('');

  const [form, setForm] = useState({
    exp_prenom: '', exp_nom: '', exp_phone: '', exp_address: '',
    dest_prenom: '', dest_nom: '', dest_phone: '', dest_address: '',
  });

  // Géocodage
  const [expSuggestions, setExpSuggestions] = useState([]);
  const [destSuggestions, setDestSuggestions] = useState([]);
  const [expCoords, setExpCoords] = useState(null);
  const [destCoords, setDestCoords] = useState(null);
  const [geocoding, setGeocoding] = useState({ exp: false, dest: false });
  const expTimerRef  = useRef(null);
  const destTimerRef = useRef(null);

  // Distance et tarif calculés
  const distance = expCoords && destCoords
    ? haversine(expCoords.lat, expCoords.lng, destCoords.lat, destCoords.lng)
    : null;
  const tarif = distance !== null ? getZoneTarif(distance) : null;

  useEffect(() => {
    let p = getStoredProfile();
    if (!p) p = getProfileForRole('particulier');
    if (!p || !['particulier', 'driver'].includes(p.role)) { navigate('/'); return; }

    if (p.role !== 'particulier') {
      const particulier = getProfileForRole('particulier');
      if (particulier) {
        p = particulier;
        setStoredProfile(particulier);
      }
    }

    setProfile(p);
    fetchDeliveries(p);
    supabase.from('profiles').select('*').eq('phone', p.phone).then(({ data }) => {
      if (data && data.length > 0) {
        setAllProfiles(data);
        setStoredProfiles(data);
        const fresh = data.find(d => d.id === p.id);
        if (fresh && fresh.role !== p.role) {
          setStoredProfile(fresh);
          setProfile(fresh);
        }
      }
    });
  }, [navigate]);

  const fetchDeliveries = async (p) => {
    setLoadingDeliveries(true);
    const { data } = await supabase.from('deliveries').select(DELIVERY_COLUMNS).eq('requester_phone', p.phone).order('created_at', { ascending: false });
    if (data) setDeliveries(data);
    setLoadingDeliveries(false);
  };

  // ---- Autocomplete adresse expéditeur ----
  const handleExpAddressChange = (value) => {
    setForm(f => ({ ...f, exp_address: value }));
    setExpCoords(null);
    setExpSuggestions([]);
    clearTimeout(expTimerRef.current);
    if (value.length < 3) return;
    expTimerRef.current = setTimeout(async () => {
      setGeocoding(g => ({ ...g, exp: true }));
      try { setExpSuggestions(await geocode(value)); } catch {}
      setGeocoding(g => ({ ...g, exp: false }));
    }, 500);
  };

  const selectExpSuggestion = (s) => {
    setForm(f => ({ ...f, exp_address: s.display_name }));
    setExpCoords({ lat: parseFloat(s.lat), lng: parseFloat(s.lon) });
    setExpSuggestions([]);
  };

  // ---- Autocomplete adresse destinataire ----
  const handleDestAddressChange = (value) => {
    setForm(f => ({ ...f, dest_address: value }));
    setDestCoords(null);
    setDestSuggestions([]);
    clearTimeout(destTimerRef.current);
    if (value.length < 3) return;
    destTimerRef.current = setTimeout(async () => {
      setGeocoding(g => ({ ...g, dest: true }));
      try { setDestSuggestions(await geocode(value)); } catch {}
      setGeocoding(g => ({ ...g, dest: false }));
    }, 500);
  };

  const selectDestSuggestion = (s) => {
    setForm(f => ({ ...f, dest_address: s.display_name }));
    setDestCoords({ lat: parseFloat(s.lat), lng: parseFloat(s.lon) });
    setDestSuggestions([]);
  };

  const handleSubmit = async () => {
    if (!form.exp_address || !form.dest_address || !form.dest_nom) return;
    setLoading(true);
    const newId   = `WK-${Date.now().toString().slice(-4)}`;
    const amount  = tarif ? tarif.price.toString() : '1000';
    const clientName = `${form.dest_prenom} ${form.dest_nom}`.trim();
    const expediteurInfo = `Ramassage: ${form.exp_address}${form.exp_prenom ? ` · De: ${form.exp_prenom} ${form.exp_nom}` : ''}`;
    const { error } = await supabase.from('deliveries').insert({
      id: newId,
      client: clientName || profile.name,
      phone: form.dest_phone,
      address: form.dest_address,
      product_name: expediteurInfo,
      status: 'pending',
      source: 'particulier',
      amount,
      store: null,
      requester_phone: profile.phone,
    });
    setLoading(false);
    if (error) { toast('Erreur lors de la demande. Réessaie.', 'error'); return; }
    setSubmitted(true);
    fetchDeliveries(profile);
  };

  const handleNewRequest = () => {
    setForm({ exp_prenom: '', exp_nom: '', exp_phone: '', exp_address: '', dest_prenom: '', dest_nom: '', dest_phone: '', dest_address: '' });
    setExpCoords(null);
    setDestCoords(null);
    setExpSuggestions([]);
    setDestSuggestions([]);
    setSubmitted(false);
  };

  const handleLogout = () => {
    clearStoredProfileSession();
    navigate(APP_ROUTES.landing);
  };

  const handleSwitch = (p) => {
    setStoredProfile(p);
    if (p.role === 'merchant') navigate(APP_ROUTES.merchant);
    else if (p.role === 'driver') navigate(APP_ROUTES.driver);
    else navigate(APP_ROUTES.particulier);
  };

  const handleBecomeVendeur = async () => {
    if (!vendeurStoreName.trim()) return;
    setVendeurLoading(true); setVendeurError('');
    // Vérifie si un profil merchant existe déjà
    const { data: existing } = await supabase.from('profiles').select('*').eq('phone', profile.phone).eq('role', 'merchant');
    if (existing && existing.length > 0) {
      const { data: allP } = await supabase.from('profiles').select('*').eq('phone', profile.phone);
      setStoredProfile(existing[0]);
      setStoredProfiles(allP || []);
      navigate(APP_ROUTES.merchant);
      return;
    }
    const { data, error } = await supabase.from('profiles').insert({
      phone: profile.phone,
      name: profile.name,
      role: 'merchant',
      store_name: vendeurStoreName.trim(),
    }).select().single();
    if (error) { setVendeurError('Erreur. Réessaie.'); setVendeurLoading(false); return; }
    const { data: allP } = await supabase.from('profiles').select('*').eq('phone', profile.phone);
    setStoredProfile(data);
    setStoredProfiles(allP || []);
    setVendeurLoading(false);
    navigate(APP_ROUTES.merchant);
  };

  const firstName   = profile?.name?.split(' ')[0] || profile?.name || '';
  const pendingCount = deliveries.filter(d => d.status === 'pending').length;
  const canSubmit   = form.exp_address && form.dest_address && form.dest_nom && !loading;

  const inputStyle = {
    width: '100%', padding: '12px 16px', border: '1.5px solid #e2e8f0',
    borderRadius: '12px', fontSize: '14px', outline: 'none',
    background: 'white', fontFamily: 'Alan Sans, sans-serif', boxSizing: 'border-box',
    transition: 'border-color 0.15s',
  };

  const suggestionListStyle = {
    position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 100,
    background: 'white', borderRadius: '12px', border: '1.5px solid #e2e8f0',
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden',
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f0f3f8' }}>

      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #07162d 0%, #112c52 100%)', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 8px 24px rgba(0,0,0,0.24)' }}>
        <button onClick={() => setShowMenu(true)}
          style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.18)', cursor: 'pointer', color: 'white', padding: '9px', borderRadius: '10px', display: 'flex' }}>
          <Menu size={20} />
        </button>
        <div style={{ width: '180px', height: '56px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <img
            src="/logo-transparent.png"
            alt="Wakaana Express"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              filter: 'drop-shadow(0 2px 12px rgba(0,0,0,0.3))',
              transform: 'scale(1.45)',
              transformOrigin: 'center center',
            }}
          />
        </div>
        {profile?.role === 'driver' ? (
          <button onClick={() => navigate(APP_ROUTES.driver)}
            style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', cursor: 'pointer', color: '#15803d', padding: '9px', borderRadius: '10px', display: 'flex' }}>
            <Truck size={18} />
          </button>
        ) : (
          <div style={{ width: '38px' }} />
        )}
      </div>

      {/* Menu latéral */}
      {showMenu && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 40, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
            onClick={() => setShowMenu(false)} />
          <div style={{ position: 'fixed', top: 0, left: 0, bottom: 0, width: '280px', zIndex: 50, background: 'linear-gradient(180deg, #07162d 0%, #112c52 100%)', display: 'flex', flexDirection: 'column', boxShadow: '12px 0 40px rgba(0,0,0,0.4)' }}>
            <div style={{ padding: '24px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'linear-gradient(135deg, #173b78, #2a66b8)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ fontSize: '16px', fontWeight: 800, color: 'white' }}>{profile?.name?.[0]}</span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '14px', fontWeight: 700, color: 'white', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{profile?.name}</div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginTop: '1px' }}>Particulier</div>
              </div>
              <button onClick={() => setShowMenu(false)}
                style={{ background: 'rgba(255,255,255,0.08)', border: 'none', cursor: 'pointer', color: 'white', padding: '7px', borderRadius: '8px', display: 'flex', flexShrink: 0 }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ flex: 1, padding: '16px', overflowY: 'auto' }}>
              {allProfiles.filter(p => p.id !== profile?.id).length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.25)', textTransform: 'uppercase', letterSpacing: '2px', fontWeight: 700, marginBottom: '10px', paddingLeft: '4px' }}>Changer de compte</div>
                  {allProfiles.filter(p => p.id !== profile?.id).map(p => (
                    <button key={p.id} onClick={() => handleSwitch(p)}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)', cursor: 'pointer', textAlign: 'left', marginBottom: '6px', transition: 'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}>
                      <div style={{ width: '34px', height: '34px', borderRadius: '9px', background: 'rgba(23,59,120,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <RefreshCw size={14} color="white" />
                      </div>
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: 'white' }}>{p.role === 'merchant' ? p.store_name : p.name}</div>
                        <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)' }}>{p.role === 'merchant' ? 'E-commerçant' : p.role === 'driver' ? 'Livreur' : 'Particulier'}</div>
                      </div>
                    </button>
                  ))}
                  <div style={{ height: '1px', background: 'rgba(255,255,255,0.06)', margin: '12px 0' }} />
                </div>
              )}
              {profile?.role === 'driver' && (
                <>
                  <button onClick={() => { setShowMenu(false); navigate(APP_ROUTES.driver); }}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', borderRadius: '10px', border: '1px solid rgba(34,197,94,0.3)', background: 'rgba(34,197,94,0.08)', cursor: 'pointer', textAlign: 'left', marginBottom: '8px' }}>
                    <div style={{ width: '34px', height: '34px', borderRadius: '9px', background: 'rgba(34,197,94,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Truck size={15} color="#22c55e" />
                    </div>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: 'white' }}>Mode Livreur</div>
                      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)' }}>Passer sur l'interface livreur</div>
                    </div>
                  </button>
                  <div style={{ height: '1px', background: 'rgba(255,255,255,0.06)', margin: '4px 0 12px' }} />
                </>
              )}
              {/* ── Devenir vendeur ── */}
              {!allProfiles.some(p => p.role === 'merchant') && (
                <>
                  <button onClick={() => { setShowVendeurForm(v => !v); setVendeurError(''); }}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '13px 14px', borderRadius: '12px', border: 'none', background: '#0f2a1a', cursor: 'pointer', textAlign: 'left', marginBottom: '8px', transition: 'opacity 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'linear-gradient(135deg, #15803d, #166534)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Store size={16} color="white" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: 'white' }}>Devenir vendeur</div>
                      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>Ouvrir ma boutique en ligne</div>
                    </div>
                    <ChevronDown size={14} color="rgba(255,255,255,0.4)" style={{ transform: showVendeurForm ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                  </button>

                  {showVendeurForm && (
                    <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '10px', padding: '14px', marginBottom: '8px', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginBottom: '8px' }}>Nom de ta boutique</div>
                      <input
                        value={vendeurStoreName}
                        onChange={e => setVendeurStoreName(e.target.value)}
                        placeholder="Ex: Boutique Awa"
                        style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.08)', color: 'white', fontSize: '13px', outline: 'none', boxSizing: 'border-box', marginBottom: '8px' }}
                      />
                      {vendeurError && <div style={{ fontSize: '11px', color: '#f87171', marginBottom: '8px' }}>{vendeurError}</div>}
                      <button onClick={handleBecomeVendeur} disabled={vendeurLoading || !vendeurStoreName.trim()}
                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: 'none', background: vendeurLoading || !vendeurStoreName.trim() ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg, #15803d, #166534)', color: 'white', fontSize: '12px', fontWeight: 700, cursor: vendeurLoading || !vendeurStoreName.trim() ? 'not-allowed' : 'pointer' }}>
                        {vendeurLoading ? 'Création...' : 'Créer ma boutique'}
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* ── Options secondaires ── */}
              <div style={{ height: '1px', background: 'rgba(255,255,255,0.06)', margin: '8px 0 12px' }} />
              {[
                { icon: Settings,    label: 'Paramètres',    sub: 'Bientôt disponible' },
                { icon: HelpCircle,  label: 'Aide & Support', sub: 'Bientôt disponible' },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', borderRadius: '10px', marginBottom: '4px', opacity: 0.4 }}>
                    <div style={{ width: '34px', height: '34px', borderRadius: '9px', background: 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon size={15} color="rgba(255,255,255,0.6)" />
                    </div>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: 'white' }}>{item.label}</div>
                      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)' }}>{item.sub}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ padding: '16px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <button onClick={handleLogout}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', borderRadius: '10px', border: '1px solid rgba(197,58,45,0.4)', background: 'rgba(197,58,45,0.1)', cursor: 'pointer', color: '#f87171', fontSize: '13px', fontWeight: 600 }}>
                <LogOut size={15} /> Se déconnecter
              </button>
            </div>
          </div>
        </>
      )}

      <div style={{ padding: '20px 16px' }}>

        {/* Bonjour */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>Bonjour</div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: '#0f172a', fontFamily: 'Alan Sans, sans-serif', lineHeight: 1.2 }}>{firstName}</div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '6px', padding: '4px 10px', borderRadius: '999px', background: '#eff6ff', border: '1px solid #bfdbfe', fontSize: '11px', fontWeight: 700, color: '#1d4ed8' }}>
            <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#1d4ed8' }} />
            Service Wakaana
          </div>
          {pendingCount > 0 && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', marginTop: '6px', padding: '4px 10px', borderRadius: '8px', background: '#fff7ed', border: '1px solid #fed7aa', fontSize: '12px', fontWeight: 600, color: '#c2410c' }}>
              <Clock size={11} /> {pendingCount} livraison{pendingCount > 1 ? 's' : ''} en attente
            </div>
          )}
        </div>

        {/* Choix service */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
          <button
            onClick={() => setSelectedService('livraison')}
            style={{
              border: selectedService === 'livraison' ? '2px solid #1d4ed8' : '1.5px solid #e2e8f0',
              background: selectedService === 'livraison' ? 'linear-gradient(135deg, #eff6ff, #dbeafe)' : 'white',
              borderRadius: '14px',
              padding: '12px',
              textAlign: 'left',
              cursor: 'pointer',
            }}
          >
            <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: 'linear-gradient(135deg, #1d4ed8, #1e40af)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '8px' }}>
              <Package size={16} color="white" />
            </div>
            <div style={{ fontSize: '13px', fontWeight: 800, color: '#0f172a' }}>Livraison</div>
            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>Envoyer un colis</div>
          </button>

          <button
            onClick={() => setSelectedService('food')}
            style={{
              border: selectedService === 'food' ? '2px solid #1d4ed8' : '1.5px solid #e2e8f0',
              background: selectedService === 'food' ? 'linear-gradient(135deg, #eff6ff, #dbeafe)' : 'white',
              borderRadius: '14px',
              padding: '12px',
              textAlign: 'left',
              cursor: 'pointer',
              position: 'relative',
            }}
          >
            <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: 'linear-gradient(135deg, #1d4ed8, #1e40af)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '8px' }}>
              <UtensilsCrossed size={16} color="white" />
            </div>
            <div style={{ fontSize: '13px', fontWeight: 800, color: '#0f172a' }}>Food</div>
            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>Bientôt disponible</div>
            <div style={{ position: 'absolute', top: '10px', right: '10px', fontSize: '9px', fontWeight: 700, color: '#1d4ed8', background: '#dbeafe', border: '1px solid #bfdbfe', borderRadius: '999px', padding: '2px 6px' }}>
              SOON
            </div>
          </button>
        </div>

        {selectedService === 'food' && (
          <div style={{ marginBottom: '16px', borderRadius: '12px', border: '1px solid #bfdbfe', background: '#eff6ff', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <UtensilsCrossed size={14} color="#1d4ed8" />
            <div style={{ fontSize: '12px', color: '#1e3a8a', fontWeight: 600 }}>
              La commande Food est en préparation. Pour l’instant, utilise “Livraison”.
            </div>
          </div>
        )}

        {/* Confirmation */}
        {submitted ? (
          <div style={{ background: 'white', borderRadius: '20px', padding: '32px 24px', textAlign: 'center', border: '1px solid #eef0f6', boxShadow: '0 4px 20px rgba(19,40,72,0.08)' }}>
            <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: 'linear-gradient(135deg, #22c55e, #15803d)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', boxShadow: '0 8px 24px rgba(34,197,94,0.3)' }}>
              <CheckCircle size={36} color="white" />
            </div>
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#0f172a', fontFamily: 'Alan Sans, sans-serif', marginBottom: '8px' }}>Demande envoyée !</div>
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '28px', lineHeight: '1.6' }}>
              Nous avons bien reçu ta demande. Un livreur va prendre en charge ton colis très prochainement.
            </p>
            <button onClick={handleNewRequest}
              style={{ padding: '12px 28px', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #173b78, #2a66b8)', color: 'white', fontSize: '14px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(23,59,120,0.3)' }}>
              Nouvelle demande
            </button>
          </div>

        ) : (

        /* Formulaire */
        selectedService === 'livraison' && (
        <div style={{ background: 'white', borderRadius: '20px', padding: '24px', border: '1px solid #dbeafe', boxShadow: '0 6px 20px rgba(29,78,216,0.09)' }}>
          <div className="ui-sheet-title" style={{ marginBottom: '20px' }}>Nouvelle demande de livraison</div>

          {/* Section Expéditeur */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
            <div style={{ padding: '4px 12px', borderRadius: '8px', background: '#eff6ff', border: '1px solid #bfdbfe', fontSize: '11px', fontWeight: 700, color: '#1d4ed8' }}>Expéditeur</div>
            <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Prénom</label>
              <input value={form.exp_prenom} onChange={e => setForm(f => ({ ...f, exp_prenom: e.target.value }))}
                style={inputStyle} placeholder="Oumar"
                onFocus={e => e.target.style.borderColor = '#173b78'}
                onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Nom</label>
              <input value={form.exp_nom} onChange={e => setForm(f => ({ ...f, exp_nom: e.target.value }))}
                style={inputStyle} placeholder="Diallo"
                onFocus={e => e.target.style.borderColor = '#173b78'}
                onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
            </div>
          </div>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Téléphone</label>
            <input value={form.exp_phone} onChange={e => setForm(f => ({ ...f, exp_phone: e.target.value }))} type="tel"
              style={inputStyle} placeholder="77 000 00 00"
              onFocus={e => e.target.style.borderColor = '#173b78'}
              onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
          </div>

          {/* Adresse expéditeur avec autocomplete */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Adresse de ramassage *</label>
            <div style={{ position: 'relative' }}>
              <MapPin size={15} color={expCoords ? '#22c55e' : '#9ca3af'} style={{ position: 'absolute', left: '14px', top: '14px', zIndex: 1 }} />
              <input
                value={form.exp_address}
                onChange={e => handleExpAddressChange(e.target.value)}
                style={{ ...inputStyle, paddingLeft: '40px', borderColor: expCoords ? '#bbf7d0' : '#e2e8f0' }}
                placeholder="Point de départ (ex: Liberté 6)"
                onFocus={e => { e.target.style.borderColor = '#173b78'; }}
                onBlur={e => { setTimeout(() => setExpSuggestions([]), 200); e.target.style.borderColor = expCoords ? '#bbf7d0' : '#e2e8f0'; }}
              />
              {geocoding.exp && (
                <div style={{ position: 'absolute', right: '14px', top: '14px', fontSize: '10px', color: '#9ca3af' }}>Recherche...</div>
              )}
              {expSuggestions.length > 0 && (
                <div style={suggestionListStyle}>
                  {expSuggestions.map((s, i) => (
                    <button key={i} onMouseDown={() => selectExpSuggestion(s)}
                      style={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 14px', border: 'none', background: 'white', cursor: 'pointer', textAlign: 'left', borderBottom: i < expSuggestions.length - 1 ? '1px solid #f0f0f0' : 'none' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                      <MapPin size={12} color="#9ca3af" style={{ marginTop: '2px', flexShrink: 0 }} />
                      <span style={{ fontSize: '12px', color: '#374151', lineHeight: '1.4' }}>{s.display_name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {expCoords && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '5px', fontSize: '10px', color: '#15803d', fontWeight: 600 }}>
                <CheckCircle size={10} /> Adresse localisée
              </div>
            )}
          </div>

          {/* Section Destinataire */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
            <div style={{ padding: '4px 12px', borderRadius: '8px', background: '#fff7ed', border: '1px solid #fed7aa', fontSize: '11px', fontWeight: 700, color: '#c2410c' }}>Destinataire</div>
            <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Prénom</label>
              <input value={form.dest_prenom} onChange={e => setForm(f => ({ ...f, dest_prenom: e.target.value }))}
                style={inputStyle} placeholder="Aminata"
                onFocus={e => e.target.style.borderColor = '#173b78'}
                onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Nom *</label>
              <input value={form.dest_nom} onChange={e => setForm(f => ({ ...f, dest_nom: e.target.value }))}
                style={inputStyle} placeholder="Sow"
                onFocus={e => e.target.style.borderColor = '#173b78'}
                onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
            </div>
          </div>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Téléphone</label>
            <input value={form.dest_phone} onChange={e => setForm(f => ({ ...f, dest_phone: e.target.value }))} type="tel"
              style={inputStyle} placeholder="78 000 00 00"
              onFocus={e => e.target.style.borderColor = '#173b78'}
              onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
          </div>

          {/* Adresse destinataire avec autocomplete */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Adresse de livraison *</label>
            <div style={{ position: 'relative' }}>
              <MapPin size={15} color={destCoords ? '#c2410c' : '#c53a2d'} style={{ position: 'absolute', left: '14px', top: '14px', zIndex: 1 }} />
              <input
                value={form.dest_address}
                onChange={e => handleDestAddressChange(e.target.value)}
                style={{ ...inputStyle, paddingLeft: '40px', borderColor: destCoords ? '#fed7aa' : '#e2e8f0' }}
                placeholder="Destination (ex: Almadies, Dakar)"
                onFocus={e => { e.target.style.borderColor = '#c53a2d'; }}
                onBlur={e => { setTimeout(() => setDestSuggestions([]), 200); e.target.style.borderColor = destCoords ? '#fed7aa' : '#e2e8f0'; }}
              />
              {geocoding.dest && (
                <div style={{ position: 'absolute', right: '14px', top: '14px', fontSize: '10px', color: '#9ca3af' }}>Recherche...</div>
              )}
              {destSuggestions.length > 0 && (
                <div style={suggestionListStyle}>
                  {destSuggestions.map((s, i) => (
                    <button key={i} onMouseDown={() => selectDestSuggestion(s)}
                      style={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 14px', border: 'none', background: 'white', cursor: 'pointer', textAlign: 'left', borderBottom: i < destSuggestions.length - 1 ? '1px solid #f0f0f0' : 'none' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                      <MapPin size={12} color="#9ca3af" style={{ marginTop: '2px', flexShrink: 0 }} />
                      <span style={{ fontSize: '12px', color: '#374151', lineHeight: '1.4' }}>{s.display_name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {destCoords && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '5px', fontSize: '10px', color: '#c2410c', fontWeight: 600 }}>
                <CheckCircle size={10} /> Adresse localisée
              </div>
            )}
          </div>

          {/* Tarif calculé */}
          {tarif ? (
            <div style={{ marginBottom: '20px', borderRadius: '14px', border: `2px solid ${tarif.border}`, background: tarif.bg, padding: '14px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: tarif.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Navigation size={16} color="white" />
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: tarif.color }}>{tarif.label}</div>
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '1px' }}>
                      Distance estimée : {distance.toFixed(1)} km ({tarif.detail})
                    </div>
                  </div>
                </div>
                <div style={{ fontFamily: 'Alan Sans, sans-serif', fontSize: '18px', fontWeight: 800, color: tarif.color, flexShrink: 0 }}>
                  {tarif.price.toLocaleString('fr-FR')} F
                </div>
              </div>
            </div>
          ) : (form.exp_address && form.dest_address && !expCoords && !destCoords) ? null : (
            (form.exp_address || form.dest_address) && !(expCoords && destCoords) && (
              <div style={{ marginBottom: '20px', borderRadius: '12px', border: '1px dashed #e2e8f0', background: '#f8fafc', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <AlertCircle size={14} color="#9ca3af" />
                <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                  Sélectionne les deux adresses dans les suggestions pour voir le tarif.
                </div>
              </div>
            )
          )}

          <button onClick={handleSubmit} disabled={!canSubmit}
            style={{ width: '100%', padding: '14px', borderRadius: '12px', border: 'none', background: !canSubmit ? '#e5e7eb' : 'linear-gradient(135deg, #c53a2d, #8b1e16)', color: !canSubmit ? '#9ca3af' : 'white', fontSize: '14px', fontWeight: 700, cursor: !canSubmit ? 'not-allowed' : 'pointer', transition: 'all 0.15s', boxShadow: !canSubmit ? 'none' : '0 4px 16px rgba(197,58,45,0.3)' }}>
            {loading ? 'Envoi en cours...' : tarif ? `Envoyer · ${tarif.price.toLocaleString('fr-FR')} F` : 'Envoyer ma demande'}
          </button>
        </div>
        )
        )}

        {/* Mes livraisons */}
        <div style={{ marginTop: '20px' }}>
          <button onClick={() => setShowHistory(!showHistory)}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'white', border: '1px solid #eef0f6', borderRadius: '14px', padding: '16px', cursor: 'pointer', boxShadow: '0 2px 8px rgba(19,40,72,0.05)', transition: 'box-shadow 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(19,40,72,0.1)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(19,40,72,0.05)'}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'linear-gradient(135deg, #173b78, #2a66b8)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Package size={16} color="white" />
              </div>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>Mes livraisons</div>
                <div style={{ fontSize: '11px', color: '#9ca3af' }}>{deliveries.length} commande{deliveries.length > 1 ? 's' : ''}</div>
              </div>
            </div>
            <ChevronRight size={16} color="#9ca3af" style={{ transform: showHistory ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />
          </button>

          {showHistory && (
            <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {loadingDeliveries ? (
                [0,1,2].map(i => <SkeletonRow key={i} />)
              ) : deliveries.length === 0 ? (
                <div style={{ background: 'white', borderRadius: '12px', padding: '32px', textAlign: 'center', border: '1px solid #f0f0f0' }}>
                  <div style={{ fontSize: '13px', color: '#9ca3af' }}>Aucune livraison pour l'instant</div>
                </div>
              ) : (
                deliveries.map(d => {
                  const s = statusConfig[d.status] || statusConfig.pending;
                  const Icon = s.icon;
                  const paid = d.payment_status === 'paid';
                  return (
                    <div key={d.id} style={{ background: 'white', borderRadius: '12px', padding: '14px 16px', border: `1px solid ${s.border}`, display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: s.bg, border: `1px solid ${s.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Icon size={16} color={s.color} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '11px', fontWeight: 700, color: '#173b78', fontFamily: 'monospace' }}>{d.id}</span>
                          <span style={{ fontSize: '10px', fontWeight: 700, padding: '1px 7px', borderRadius: '6px', background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>{s.label}</span>
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.address}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                            {formatOrderTime(d.created_at)}
                          </span>
                          {d.amount && (
                            <span style={{ fontSize: '11px', fontWeight: 700, padding: '1px 7px', borderRadius: '6px', background: paid ? '#f0fdf4' : '#fff7ed', color: paid ? '#15803d' : '#c2410c', border: `1px solid ${paid ? '#bbf7d0' : '#fed7aa'}` }}>
                              {paid ? 'Payé' : 'Impayé'}
                            </span>
                          )}
                        </div>
                      </div>
                      {d.amount && (
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontSize: '14px', fontWeight: 800, color: '#173b78' }}>{Number(d.amount).toLocaleString('fr-FR')} F</div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
